// WizardPipeline — drill-down, tap-only entry path.
//
// Flow: location → tile → canonical → brand → package size.
// Reuses the proven multi-step pattern from ShopModePlacementWizard
// but adds a brand step before package size, and writes through the
// AddItemDraftStore instead of bespoke ShopMode commit logic.
//
// Each step is tap-only — no typing required (canonical step has
// optional search via RisingResults for power users, but the default
// is scrollable list of in-tile canonicals).
//
// Why a separate component:
//   - The user explicitly asked for a "no typing" path.
//   - The drill-down is its own UX; mixing it into ManualPipeline
//     would muddy both.

import { useState, useMemo, useRef, useEffect } from "react";
import { useDraftStore } from "./store";
import { canonicalAutofillSync } from "../../../lib/canonicalAutofill";
import { findIngredient, hubForIngredient, findHub, membersOfHub, allCanonicals, HUBS } from "../../../data/ingredients";
import { useIngredientInfo } from "../../../lib/useIngredientInfo";
import { brandsForCanonical } from "../../../data/brandExpertise";
import { useShoppingRegion } from "../../../lib/useShoppingRegion";
import { FRIDGE_TILES, tileIdForItem as fridgeTileFor } from "../../../lib/fridgeTiles";
import { PANTRY_TILES, pantryTileIdForItem as pantryTileFor } from "../../../lib/pantryTiles";
import { FREEZER_TILES, freezerTileIdForItem as freezerTileFor } from "../../../lib/freezerTiles";
import { isGenericOnly } from "./genericCanonicals";
import RisingResults from "./RisingResults";
import PackageSizeStep from "./PackageSizeStep";
import BrandStep from "./BrandStep";
import ScanReview from "./ScanReview";
import { font, color, radius, space } from "../../../lib/mcmTokens";
import { CHIP_TONES } from "../../../lib/tokens";

// Same swatch palette the kitchen's FloatingLocationDock uses, copied
// here so the wizard's location tabs read as physically the same
// surface — fridge dark cool blue, pantry MCM burnt orange, freezer
// icy pale blue. NOT theme-variant — these are semantic-fixed.
const LOCATION_DOT = {
  fridge:  "#2F5A85",
  pantry:  "#D96B2B",
  freezer: "#A8D8EA",
};

// The classifiers in fridgeTiles / pantryTiles / freezerTiles take
// an item-shaped object plus dependency helpers. We synthesize a
// minimal item ({ ingredientId }) from each canonical so the same
// classifier that places real pantry rows also places candidates
// here. Single source of truth — if Meat & Poultry takes "chicken"
// in the kitchen, it takes "chicken" in the wizard too.
//
// `getInfo` MUST be threaded through so the classifier can honor
// `ingredient_info.tile_routing.<location>` overrides written by
// `scripts/ai_recategorize_misc.mjs`. Without it, items that the
// AI re-routed land back in misc on every render.
function buildTileSets(getInfo) {
  const helpers = { findIngredient, hubForIngredient, getInfo };
  return {
    fridge:  {
      tiles: FRIDGE_TILES,
      classify: (ing) => fridgeTileFor({ ingredientId: ing.id, category: ing.category }, helpers),
    },
    pantry:  {
      tiles: PANTRY_TILES,
      classify: (ing) => pantryTileFor({ ingredientId: ing.id, category: ing.category }, helpers),
    },
    freezer: {
      tiles: FREEZER_TILES,
      classify: (ing) => freezerTileFor({ ingredientId: ing.id, category: ing.category }, helpers),
    },
  };
}

const LOCATIONS = [
  { id: "fridge",  label: "Fridge",  emoji: "❄️" },
  { id: "pantry",  label: "Pantry",  emoji: "🥫" },
  { id: "freezer", label: "Freezer", emoji: "🧊" },
];

export default function WizardPipeline({ store, registerBack }) {
  const draft = useDraftStore(store, (s) => s);
  const { getInfo } = useIngredientInfo();
  const [region] = useShoppingRegion();

  // Default the location to "fridge" so the wizard always opens on a
  // tab. Persistent location tabs at the top let the user switch
  // anytime — there's no longer a "Where?" step to navigate. If the
  // store carries a pre-set location (resuming a draft), respect it.
  // Source MUST be "autofill" — calling setAxis with the default
  // source="user" would mark location as overridden and lock it,
  // which prevented canonicalAutofill from later moving the wizard
  // to the canonical's natural home (e.g. arborio rice → pantry).
  useEffect(() => {
    if (!draft.location) {
      store.setAxis("location", "fridge", "autofill");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Step machine — location is gone; the wizard now opens on tile.
  // Steps: tile → group → brand → size → done.
  const [step, setStep] = useState(() => {
    if (!draft.tileId) return "tile";
    if (!draft.canonicalId) return "group";
    return "size";
  });
  // GLOBAL search — sits at the top of the wizard above everything
  // else. When non-empty it overrides the current step's contents
  // with a flat results list of every canonical in the active
  // location whose name matches. Picking a result jumps straight to
  // the brand step regardless of where the user was.
  const [globalSearch, setGlobalSearch] = useState("");
  const searchRef = useRef(null);
  // Which hub the user drilled into at the group step. null = at the
  // group level, picking between hubs and standalones.
  const [activeHubId, setActiveHubId] = useState(null);
  // Letter-jump filter — when a hub has > 10 members, surface a row
  // of first-letter chips so the user can tap "P" and only see the
  // P entries (peppers / parmesan / pearl onions / etc.) instead of
  // scrolling through 50+ items. null = no letter filter active.
  const [letterFilter, setLetterFilter] = useState(null);
  // One-line note shown on the tile step after a location-tab tap
  // that KEPT the bound item (see switchLocation). Cleared on the
  // next tile pick so it doesn't linger into unrelated flows.
  const [keptIdentityNote, setKeptIdentityNote] = useState(null);
  // Set when the user re-enters the tile step FROM the done-step
  // review to MOVE the item (tap the Tile/Location chip). pickTile's
  // tile-switching branch normally treats a different tile as a
  // context change and wipes the identity — correct mid-browse,
  // catastrophic for a placement move from the review (same nuke
  // switchLocation's fix eliminated for the tabs).
  const placementMoveRef = useRef(false);

  const tileSets = useMemo(() => buildTileSets(getInfo), [getInfo]);
  const tileSet = draft.location ? tileSets[draft.location] : null;

  // Compute the contents of the current (location, tile) — split into
  // hubs that have at least one in-tile member, and standalone canonicals
  // that aren't in a hub. Hubs render as their own tile-like cards;
  // standalones render alongside.
  //
  // Nested-hub support: a hub with `parentHub` set (e.g. pepper_hub
  // → vegetable_hub) does NOT appear at the top of the tile drilldown.
  // It only surfaces as a card INSIDE its parent hub's drilldown.
  // Otherwise the user would see Vegetables, Peppers, Roots, Legumes
  // all at the top — collapsing the visual hierarchy we just added.
  const groupOptions = useMemo(() => {
    if (!draft.location || !draft.tileId || !tileSet) return { hubs: [], standalones: [] };
    const hubMemberCount = new Map(); // hubId → count of in-tile members
    const standalones = [];
    // Both tiers — synthetic canonicals with storage/tile metadata
    // (spam → pantry/canned) surface in the drilldown like bundled
    // ones; those without placement metadata simply match no tile.
    for (const ing of allCanonicals()) {
      // Skip hub records themselves (they have no parentId AND end in _hub)
      if (ing.parentId === undefined && ing.id.endsWith("_hub")) continue;
      // Skip generic / non-bindable canonicals (chicken, beef, cheese,
      // mushroom, etc.). They're real registry entries used by alias
      // resolution and recipe matching but shouldn't be selectable in
      // the wizard — picking "chicken" with no cut gives the kitchen
      // nothing actionable for package size, nutrition, or expiry.
      if (isGenericOnly(ing.id)) continue;
      const loc = ing.storage?.location;
      if (loc && loc !== draft.location) continue;
      const tile = tileSet.classify(ing);
      if (tile !== draft.tileId) continue;
      const hub = hubForIngredient(ing);
      if (hub) {
        hubMemberCount.set(hub.id, (hubMemberCount.get(hub.id) || 0) + 1);
      } else {
        standalones.push({ id: ing.id, label: ing.name, emoji: ing.emoji });
      }
    }
    // Roll nested-hub counts UP into their parents so a "Vegetables"
    // card shows the correct total (own members + members of any
    // child hubs underneath it). Pepper/Root cards are nested inside
    // Vegetables; without rolling counts, Vegetables shows 30 but
    // there are actually 50 reachable items beneath it.
    for (const h of HUBS) {
      if (!h.parentHub) continue;
      const own = hubMemberCount.get(h.id) || 0;
      if (!own) continue;
      hubMemberCount.set(
        h.parentHub,
        (hubMemberCount.get(h.parentHub) || 0) + own
      );
    }
    let hubs = HUBS
      .filter((h) => hubMemberCount.has(h.id))
      .filter((h) => !h.parentHub) // nested hubs hide at top level
      .map((h) => ({
        id: h.id,
        label: h.name,
        emoji: h.emoji,
        sub: `${hubMemberCount.get(h.id)} variet${hubMemberCount.get(h.id) === 1 ? "y" : "ies"}`,
        isHub: true,
      }));

    // Auto-flatten umbrella hubs. When a tile has exactly ONE top-
    // level hub and that hub's name closely matches the tile's label
    // (or the hub has children), drilling the tile and then drilling
    // the hub is two hops with the same name showing twice
    // ("Spices & Dried Herbs" tile → "Spices & Dried Herbs" hub →
    // Korean Spices). Hoist the umbrella's children up to the tile
    // face so the user sees Korean Spices / Indian Spices / etc.
    // directly. Direct canonical members of the umbrella surface as
    // standalones at the same face.
    if (hubs.length === 1) {
      const umbrella = hubs[0];
      const children = HUBS.filter((h) => h.parentHub === umbrella.id);
      if (children.length > 0) {
        const childCards = children
          .filter((h) => hubMemberCount.has(h.id))
          .map((h) => ({
            id: h.id,
            label: h.name,
            emoji: h.emoji,
            sub: `${hubMemberCount.get(h.id)} variet${hubMemberCount.get(h.id) === 1 ? "y" : "ies"}`,
            isHub: true,
          }));
        // Direct canonical members of the umbrella (no sub-hub) — pull
        // them up as standalones so they remain reachable when we skip
        // the umbrella drilldown.
        for (const ing of allCanonicals()) {
          if (ing.parentId !== umbrella.id) continue;
          if (isGenericOnly(ing.id)) continue;
          const loc = ing.storage?.location;
          if (loc && loc !== draft.location) continue;
          const tile = tileSet.classify(ing);
          if (tile !== draft.tileId) continue;
          standalones.push({ id: ing.id, label: ing.name, emoji: ing.emoji });
        }
        hubs = childCards;
      }
    }

    standalones.sort((a, b) => a.label.localeCompare(b.label));
    return { hubs, standalones };
  }, [draft.location, draft.tileId, tileSet]);

  // Members of the drilled-into hub, plus any nested hubs whose
  // parentHub === activeHubId. The active hub's drilldown shows:
  //   • direct canonical members (e.g. broccoli, kale, …)
  //   • nested hub cards (Peppers, Roots, …) sitting alongside
  //
  // Nested hub cards carry isHub:true so a tap drills further into
  // them; direct members carry no flag and bind on tap.
  const hubMembers = useMemo(() => {
    if (!activeHubId) return [];
    const direct = membersOfHub(activeHubId)
      // Filter out generic / non-bindable canonicals at the hub-member
      // level too — picking "chicken" inside chicken_hub or "steak"
      // inside beef_hub doesn't give the kitchen enough signal.
      .filter((ing) => !isGenericOnly(ing.id))
      .map((ing) => ({
        id: ing.id,
        label: ing.name,
        emoji: ing.emoji,
      }));
    // Nested hubs whose parent is the current activeHubId. Only
    // surface them when at least one member exists in the current
    // tile context — empty hubs would render as dead-end cards.
    const nestedHubs = HUBS
      .filter((h) => h.parentHub === activeHubId)
      .map((h) => {
        const memberCount = membersOfHub(h.id).length;
        if (memberCount === 0) return null;
        return {
          id: h.id,
          label: h.name,
          emoji: h.emoji,
          sub: `${memberCount} variet${memberCount === 1 ? "y" : "ies"}`,
          isHub: true,
        };
      })
      .filter(Boolean);
    direct.sort((a, b) => a.label.localeCompare(b.label));
    // Hubs render first so the user sees grouping cards above the
    // long flat list of direct members.
    return [...nestedHubs, ...direct];
  }, [activeHubId]);

  // Standalones-only canonicals (the previous flat list, kept for the
  // group-step fallback path inside a hub.) No longer filtered by
  // search — the global search bar at the top of the wizard handles
  // text filtering across the whole location.
  const visibleCanonicals = useMemo(() => {
    return activeHubId ? hubMembers : groupOptions.standalones;
  }, [activeHubId, hubMembers, groupOptions.standalones]);

  // GLOBAL SEARCH — flatten every bindable canonical in the active
  // location (across every tile, every hub) into a single ranked
  // list filtered by the search query. Activated whenever
  // globalSearch is non-empty; when empty, the wizard renders its
  // normal tile/group flow.
  //
  // Search keys: canonical name + searchAliases (so "rootbeer" and
  // "coke" find the soda canonical, "ketchup" finds itself, etc.).
  // Hubs are excluded — picking a hub from a flat search result is
  // ambiguous (hub.id isn't a real canonical), and the user's
  // typed something specific anyway.
  const globalResults = useMemo(() => {
    const q = globalSearch.trim().toLowerCase();
    if (!q || !draft.location || !tileSet) return [];
    const out = [];
    const seenIds = new Set();
    // CRITICAL: iterate BOTH bundled INGREDIENTS and runtime
    // dbCanonicals — allCanonicals() is the sanctioned entry point
    // for exactly this. The earlier version only iterated the bundled
    // array, which made every user-minted synthetic canonical
    // (Memory Book photo capture, AI-discovered slugs not in the
    // bundled registry) invisible to the wizard's search.
    const universe = allCanonicals();
    for (const ing of universe) {
      if (!ing || !ing.id) continue;
      // Hubs themselves don't appear in search results.
      if (ing.parentId === undefined && ing.id?.endsWith?.("_hub")) continue;
      if (isGenericOnly(ing.id)) continue;
      // Dedupe — if the same id exists in both bundled + dbCanonicals
      // (shouldn't happen because registerCanonicalsFromDb skips
      // bundled slugs, but defensive), keep only the first sighting.
      if (seenIds.has(ing.id)) continue;
      seenIds.add(ing.id);
      const loc = ing.storage?.location;
      if (loc && loc !== draft.location) continue;
      // For canonicals without an explicit storage.location, classify
      // them through the active tile-set to make sure we're not
      // surfacing pantry items in the freezer tab.
      if (!loc) {
        const tile = tileSet.classify(ing);
        if (!tile || tile === "misc") {
          // Misc-bound items still surface — better to find a result
          // than hide it on a strict-classification miss.
        }
      }
      const haystack = [
        ing.name?.toLowerCase() || "",
        ...(Array.isArray(ing.searchAliases) ? ing.searchAliases.map(a => a.toLowerCase()) : []),
        ing.id.toLowerCase().replace(/_/g, " "),
      ];
      const hit = haystack.some((h) => h.includes(q));
      if (!hit) continue;
      // Rank: exact prefix on name first, then includes-on-name, then
      // alias hits last.
      const nameLower = ing.name?.toLowerCase() || "";
      let rank = 3;
      if (nameLower.startsWith(q)) rank = 0;
      else if (nameLower.includes(q)) rank = 1;
      else rank = 2;
      out.push({ id: ing.id, label: ing.name, emoji: ing.emoji, rank });
    }
    out.sort((a, b) => a.rank - b.rank || a.label.localeCompare(b.label));
    return out.slice(0, 60); // hard cap so the list stays scannable
  }, [globalSearch, draft.location, tileSet]);

  // Tab tap — switches the active location and resets the
  // downstream wizard state so the user starts fresh on the new
  // tab. Tile / hub are location-scoped, so keeping them across
  // a tab swap would leak fridge state into the pantry view.
  //
  // SCAN HANDOFF mode: when a canonical is already bound (the user
  // came in from a successful UPC scan and is using the wizard as
  // a placement-finisher), DON'T reset the canonical. They've
  // picked the item; they just need a location + tile. Tile still
  // resets because it's location-scoped, but canonical / brand /
  // variant / size all stay so the commit can still write the UPC
  // correction with the correct axes attached.
  function switchLocation(loc) {
    if (loc === draft.location) return;
    store.setAxis("location", loc);
    store.setAxis("tileId", null);

    // BOUND canonical → the tab tap is a PLACEMENT move, not a
    // context change. The user explicitly picked this item (browse
    // drill, scan handoff, or global search); nuking canonical +
    // brand + size because they checked another location cost a
    // full re-entry with no undo — the single worst friction hit in
    // the add-flow audit. Keep identity, clear only the location-
    // scoped tile, and say so. Hub/letter/search UI state still
    // resets (those navigate the new location's catalog).
    if (draft.canonicalId) {
      setActiveHubId(null);
      setLetterFilter(null);
      setGlobalSearch("");
      setKeptIdentityNote(
        `Kept ${draft.canonicalName || "your item"} — pick its shelf here.`,
      );
      setStep("tile");
      return;
    }

    // No canonical bound → tab tap is a fresh start; full reset is
    // correct (tile/hub are location-scoped and stale state would
    // leak fridge context into the pantry view).
    placementMoveRef.current = false;
    store.setCanonical(null, null, null);
    store.resetOverrides();
    store.setAxis("brand",         null, "autofill");
    store.setAxis("variant",       null, "autofill");
    store.setAxis("packageAmount", null, "autofill");
    store.setAxis("packageUnit",   null, "autofill");
    store.setAxis("state",         null, "autofill");
    store.setAxis("cut",           null, "autofill");
    setActiveHubId(null);
    setLetterFilter(null);
    setGlobalSearch("");
    setStep("tile");
  }

  function pickTile(tile) {
    setKeptIdentityNote(null);
    // Placement move from the done-step review: keep the whole
    // identity, just re-shelve, and return to the review.
    if (placementMoveRef.current) {
      placementMoveRef.current = false;
      store.setAxis("tileId", tile.id);
      if (tile.category) store.setAxis("category", tile.category);
      setStep("done");
      return;
    }
    // Detect tile-SWITCHING (changing tiles after having already
    // picked one) vs first-time tile selection. Switching means the
    // user is navigating to a different category — the canonical
    // they bound under the previous tile no longer applies.
    // Caused the "SEAFOOD › CHICKEN" breadcrumb bug: user went
    // Meat & Poultry → Chicken → back → Seafood and the brand
    // step still showed chicken brands because canonicalId was
    // never cleared on tile change.
    const isSwitchingTile = draft.tileId && draft.tileId !== tile.id;

    store.setAxis("tileId", tile.id);
    if (tile.category) store.setAxis("category", tile.category);
    setActiveHubId(null);
    setLetterFilter(null);

    if (isSwitchingTile) {
      // User picked a DIFFERENT tile — clear the canonical and
      // downstream state from the previous tile's flow. Force them
      // back to the group step to pick a fresh canonical under the
      // new tile.
      store.setCanonical(null, null, null);
      store.resetOverrides();
      store.setAxis("brand",         null, "autofill");
      store.setAxis("variant",       null, "autofill");
      store.setAxis("packageAmount", null, "autofill");
      store.setAxis("packageUnit",   null, "autofill");
      store.setAxis("state",         null, "autofill");
      store.setAxis("cut",           null, "autofill");
      setStep("group");
      return;
    }

    // First-time tile pick. If canonicalId is already set, we're in
    // scan-handoff mode (canonical bound by a scan before tile pick)
    // — skip group/canonical step, jump to size or done.
    if (draft.canonicalId) {
      if (!draft.packageAmount || !draft.packageUnit) {
        setStep("size");
      } else {
        setStep("done");
      }
    } else {
      setStep("group");
    }
  }

  // Group step picks: a hub (drill into members) OR a standalone
  // canonical (bind directly, skip the inner step). Same handler is
  // also used by member-view nested hub cards — tapping "Peppers"
  // inside Vegetables drills into pepper_hub.
  function pickGroupOption(opt) {
    if (opt.isHub) {
      setActiveHubId(opt.id);
      setLetterFilter(null);   // letters reset when entering a hub
      // Stay on the group step; the render switches to member view
      // when activeHubId is set.
    } else {
      pickCanonical(opt);
    }
  }

  function pickCanonical(c) {
    // If we're SWITCHING to a different canonical (user navigated
    // back from brand/size to group, then picked a new canonical),
    // clear downstream selections — brand, variant, package size
    // were tied to the previous canonical and don't apply here.
    // Without this, going Chicken → back → Fish leaves Tyson +
    // chicken's package size pre-selected on the Fish flow.
    // resetOverrides + null setAxis releases the locks so the new
    // canonical's autofill can populate fresh defaults.
    const isSwitching = draft.canonicalId && draft.canonicalId !== c.id;
    if (isSwitching) {
      store.resetOverrides();
      store.setAxis("brand",         null, "autofill");
      store.setAxis("variant",       null, "autofill");
      store.setAxis("packageAmount", null, "autofill");
      store.setAxis("packageUnit",   null, "autofill");
      // state and cut are anatomical for meats — also reset since
      // they're per-canonical (chicken cuts ≠ fish cuts).
      store.setAxis("state",         null, "autofill");
      store.setAxis("cut",           null, "autofill");
    }

    const ing = findIngredient(c.id);
    store.setCanonical(c.id, ing?.name || c.label, ing?.emoji || c.emoji);
    const dbInfo = getInfo(c.id);
    const autofill = canonicalAutofillSync(c.id, {
      brand: isSwitching ? null : draft.brand,
      dbInfo,
      name: ing?.name || c.label,
    });
    // The wizard already chose the location tab — preserve it over
    // the autofill cascade. Tile we DO let autofill set when the
    // user came from the global search (no tile picked yet).
    store.applyAutofill({
      ...autofill,
      location: undefined,
      tileId: draft.tileId ? undefined : autofill.tileId,
    });
    setGlobalSearch("");
    setActiveHubId(null);
    // Hop into the brand step before sizing — the brand picker uses
    // the canonical to load curated/observed/AI brand suggestions,
    // and once a brand is chosen the package sizes can be brand-
    // specific (Kerrygold sizes vs generic butter sizes, etc.).
    setStep("brand");
  }

  // Wire the smart-back behavior: step the user up one level
  // (canonical/done → size → brand → group → tile → entry).
  // When at "tile", returning false lets AddItemSheet fall through
  // to setMode("entry") so the user lands at the gate. Location is
  // gone — it's a persistent tab now, not a step.
  useEffect(() => {
    if (typeof registerBack !== "function") return;
    registerBack(() => {
      // Global search active? Back clears the search before stepping.
      if (globalSearch) {
        setGlobalSearch("");
        return true;
      }
      if (step === "size" || step === "done") {
        // Going back uncommits the package size. Without this, the
        // breadcrumb / chips kept showing the size value even after
        // the user navigated away. Same shape as the other back
        // steps now — back means "undo this step's pick".
        store.setAxis("packageAmount", null, "autofill");
        store.setAxis("packageUnit",   null, "autofill");
        const fresh = store.getState();
        const info = getInfo?.(fresh.canonicalId) || null;
        const catalog = brandsForCanonical(fresh.canonicalId, {
          limit: 32, info, region,
        });
        const lower = (fresh.brand || "").trim().toLowerCase();
        const match = catalog.find((s) => s.display.toLowerCase() === lower);
        if (match?.variants && match.variants.length > 0) {
          setStep("variant");
        } else {
          setStep("brand");
        }
        return true;
      }
      if (step === "variant") {
        // Uncommit variant on back.
        store.setAxis("variant", null, "autofill");
        setStep("brand");
        return true;
      }
      if (step === "brand") {
        // Uncommit brand AND canonical on back. The user is leaving
        // the brand step — they're effectively un-picking the
        // canonical they bound at the previous step. Without
        // clearing canonical, the breadcrumb keeps showing it and
        // forward navigation re-uses the stale binding.
        store.setAxis("brand",   null, "autofill");
        store.setAxis("variant", null, "autofill");
        store.setAxis("state",   null, "autofill");
        store.setAxis("cut",     null, "autofill");
        store.setCanonical(null, null, null);
        setStep("group");
        return true;
      }
      if (step === "group") {
        if (activeHubId) {
          const current = findHub(activeHubId);
          if (current?.parentHub) {
            setActiveHubId(current.parentHub);
            return true;
          }
          setActiveHubId(null);
          return true;
        }
        // Back from face A of group — uncommit tile + category,
        // return to tile picker. Without this, the breadcrumb still
        // shows the tile name even though the user has navigated
        // back to choose a different one.
        store.setAxis("tileId",   null, "autofill");
        store.setAxis("category", null, "autofill");
        setStep("tile");
        return true;
      }
      // "tile" → not handled; parent kicks back to EntryGate.
      return false;
    });
    return () => { registerBack(null); };
  }, [registerBack, step, activeHubId]);

  // GLOBAL search active — flat results list across the active
  // location's tile set. Renders INSTEAD of the step contents below.
  const showGlobalResults = globalSearch.trim().length > 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <LocationTabs
        active={draft.location || "fridge"}
        onSelect={switchLocation}
      />
      <input
        ref={searchRef}
        type="text"
        value={globalSearch}
        onChange={(e) => setGlobalSearch(e.target.value)}
        placeholder="Search your kitchen…"
        style={{
          width: "100%",
          padding: "12px 14px",
          fontFamily: font.sans,
          fontSize: 14,
          color: color.ink,
          background: color.cream,
          border: `1px solid ${color.hairline}`,
          borderRadius: radius.field || 12,
          outline: "none",
        }}
      />

      {!showGlobalResults && (
        <Crumbs
          draft={draft}
          step={step}
          onJump={(target) => {
            if (target === "group") setActiveHubId(null);
            setStep(target);
          }}
          activeHub={activeHubId ? findHub(activeHubId)?.name : null}
        />
      )}

      {showGlobalResults && (
        <Step
          kicker={`${globalResults.length} match${globalResults.length === 1 ? "" : "es"}`}
          title={globalResults.length > 0
            ? `Pick one in ${draft.location || "your kitchen"}.`
            : `Nothing matches in ${draft.location || "your kitchen"}.`}
          accent={CHIP_TONES.canonical.fg}
        >
          {globalResults.length === 0 ? (
            <div style={{
              fontFamily: font.sans,
              fontSize: 13,
              color: color.inkMuted,
              textAlign: "center",
              padding: "24px 0",
            }}>
              Try a different tab, or clear the search to browse shelves.
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {globalResults.map((c) => (
                <button
                  key={c.id}
                  onClick={(e) => { e.preventDefault(); pickCanonical(c); }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "10px 12px",
                    fontFamily: font.itemTitle,
                    fontStyle: "italic",
                    fontSize: 15,
                    fontWeight: 500,
                    color: color.ink,
                    background: color.cream,
                    border: `1px solid ${color.hairline}`,
                    borderRadius: radius.field || 12,
                    cursor: "pointer",
                    textAlign: "left",
                    minHeight: 56,
                  }}
                >
                  {c.emoji && <span style={{ fontSize: 20 }}>{c.emoji}</span>}
                  <span style={{
                    flex: 1,
                    minWidth: 0,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    lineHeight: 1.2,
                  }}>
                    {c.label}
                  </span>
                </button>
              ))}
            </div>
          )}
        </Step>
      )}

      {!showGlobalResults && step === "tile" && tileSet && (
        <Step kicker="STORED IN" title="Which shelf?" accent={CHIP_TONES.location.fg}>
          {keptIdentityNote && (
            <div style={{
              marginBottom: 10,
              padding: "8px 12px",
              borderRadius: radius.field || 10,
              background: `${CHIP_TONES.canonical.fg}12`,
              border: `1px solid ${CHIP_TONES.canonical.fg}44`,
              fontFamily: font.detail,
              fontStyle: "italic",
              fontSize: 13,
              color: color.ink,
            }}>
              {keptIdentityNote}
            </div>
          )}
          <Tiles
            options={tileSet.tiles.map((t) => ({
              id: t.id,
              label: t.label,
              emoji: t.emoji,
              sub: t.blurb,
            }))}
            onPick={pickTile}
          />
        </Step>
      )}

      {!showGlobalResults && step === "group" && (() => {
        // Two faces of this step:
        //   A. activeHubId === null → render hubs + standalones as
        //      tile-like cards. User picks a hub to drill, or a
        //      standalone to skip directly to size.
        //   B. activeHubId set → render the chosen hub's members.
        //      A back affordance returns to face A.
        const onHubFace = activeHubId === null;
        const hub = activeHubId ? findHub(activeHubId) : null;
        const tiles = onHubFace
          ? [...groupOptions.hubs, ...groupOptions.standalones]
          : visibleCanonicals;
        const stepKicker = onHubFace ? "WHAT" : (hub?.name || "VARIETY").toUpperCase();
        const stepTitle  = onHubFace
          ? "Pick the item or family."
          : `Which ${hub?.name?.toLowerCase() || "variety"}?`;
        return (
          <Step kicker={stepKicker} title={stepTitle} accent={CHIP_TONES.canonical.fg}>
            {!onHubFace && (
              <button
                onClick={(e) => { e.preventDefault(); setActiveHubId(null); }}
                style={{
                  alignSelf: "flex-start",
                  marginBottom: 4,
                  padding: "4px 0",
                  fontFamily: font.mono,
                  fontSize: 10,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: color.inkMuted,
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                ← Back to families
              </button>
            )}
            {(() => {
              // Compute available first letters from current tile set.
              // The strip only renders letters that actually have entries
              // — tap "X" never shows up if no items start with X.
              const availableLetters = (() => {
                const set = new Set();
                for (const t of tiles) {
                  const ch = (t.label || "").trim().charAt(0).toUpperCase();
                  if (ch && /[A-Z0-9]/.test(ch)) set.add(ch);
                }
                return [...set].sort();
              })();

              // Letter-jump filter only — text filtering is handled by
              // the global search bar at the top of the wizard, which
              // surfaces matches across every tile in the active
              // location (cross-tile, not just this hub).
              const filtered = tiles
                .filter((t) =>
                  !letterFilter ||
                  (t.label || "").trim().charAt(0).toUpperCase() === letterFilter
                );

              return (
                <>
                  {/* First-letter jump strip — only when there's enough
                      to scroll past. Threshold: 10 entries. Hub cards
                      count alongside leaf canonicals. */}
                  {tiles.length > 10 && availableLetters.length > 1 && (
                    <div style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 4,
                      paddingBottom: 6,
                    }}>
                      {availableLetters.map((L) => {
                        const active = letterFilter === L;
                        return (
                          <button
                            key={L}
                            onClick={(e) => {
                              e.preventDefault();
                              setLetterFilter(active ? null : L);
                            }}
                            style={{
                              minWidth: 30,
                              height: 30,
                              padding: "0 8px",
                              fontFamily: font.mono,
                              fontSize: 12,
                              fontWeight: active ? 600 : 500,
                              color: active ? color.cream : color.ink,
                              background: active ? color.ink : `${CHIP_TONES.canonical.fg}1a`,
                              border: `1px solid ${active ? color.ink : `${CHIP_TONES.canonical.fg}40`}`,
                              borderRadius: 8,
                              cursor: "pointer",
                            }}
                          >
                            {L}
                          </button>
                        );
                      })}
                      {letterFilter && (
                        <button
                          onClick={(e) => { e.preventDefault(); setLetterFilter(null); }}
                          style={{
                            height: 30,
                            padding: "0 10px",
                            fontFamily: font.sans,
                            fontSize: 11,
                            color: color.inkMuted,
                            background: "transparent",
                            border: `1px solid ${color.hairline}`,
                            borderRadius: 8,
                            cursor: "pointer",
                          }}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  )}

                  {filtered.length === 0 ? (
                    <div style={{
                      fontFamily: font.sans,
                      fontSize: 13,
                      color: color.inkMuted,
                      textAlign: "center",
                      padding: "16px 0",
                    }}>
                      {letterFilter
                        ? `No items start with "${letterFilter}".`
                        : "Nothing classified here yet."}
                    </div>
                  ) : (
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 8,
                      // No maxHeight / overflow here — the parent sheet body
                      // already scrolls. Nesting a second scroll region
                      // created the "dead space" effect in screenshots.
                    }}>
                      {filtered.map((c) => (
                  <button
                    key={c.id}
                    onClick={(e) => {
                      e.preventDefault();
                      // Nested hub cards (isHub:true) appear in BOTH the
                      // top-of-tile face AND inside parent-hub member
                      // views (e.g. Peppers card lives both at the top
                      // of Produce-with-no-nesting AND inside Vegetables
                      // when nested). Either way, tapping a hub means
                      // "drill into it" — never bind it as a canonical,
                      // since hub ids like "pepper_hub" aren't real
                      // canonicals and would crash the size step.
                      if (c.isHub) {
                        pickGroupOption(c);
                      } else {
                        pickCanonical(c);
                      }
                    }}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "stretch",
                      justifyContent: "center",
                      gap: 4,
                      padding: "10px 12px",
                      fontFamily: font.itemTitle,
                      fontStyle: "italic",
                      fontSize: 15,
                      color: color.ink,
                      background: c.isHub ? `${CHIP_TONES.canonical.fg}10` : color.cream,
                      border: `1px solid ${c.isHub ? `${CHIP_TONES.canonical.fg}55` : color.hairline}`,
                      borderRadius: radius.field || 12,
                      cursor: "pointer",
                      textAlign: "left",
                      minHeight: 80,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {c.emoji && (
                        <span style={{ fontSize: c.isHub ? 22 : 20 }}>{c.emoji}</span>
                      )}
                      <span style={{
                        flex: 1,
                        minWidth: 0,
                        // Allow wrapping to 2 lines instead of truncating —
                        // "Chicken Tenderloins" / "Chicken Nuggets" need
                        // the second line. Hard-cap at 2 to keep tile
                        // heights consistent across the grid.
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        lineHeight: 1.2,
                        fontWeight: 500,
                      }}>
                        {c.label}
                      </span>
                      {c.isHub && (
                        <svg
                          width="10" height="10" viewBox="0 0 10 10"
                          style={{ flexShrink: 0, color: CHIP_TONES.canonical.fg, opacity: 0.6 }}
                          fill="none" stroke="currentColor" strokeWidth="2"
                          strokeLinecap="round" strokeLinejoin="round"
                        >
                          <polyline points="3 1 7 5 3 9" />
                        </svg>
                      )}
                    </div>
                    {c.sub && (
                      <div style={{
                        fontFamily: font.mono,
                        fontStyle: "normal",
                        fontSize: 10,
                        letterSpacing: "0.10em",
                        textTransform: "uppercase",
                        color: color.inkMuted,
                      }}>
                        {c.sub}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
                </>
              );
            })()}
          </Step>
        );
      })()}

      {!showGlobalResults && step === "brand" && (
        <Step kicker="BRAND" title="Which brand?" accent={CHIP_TONES.canonical.fg}>
          <BrandStep
            canonicalId={draft.canonicalId}
            value={draft.brand}
            variantValue={draft.variant}
            onChange={(b) => store.setAxis("brand", b)}
            onVariant={(v) => store.setAxis("variant", v)}
            inlineVariants={false}
            onCommit={() => {
              // Read FRESH state from the store. BrandStep fires
              // onChange + onCommit back-to-back; the closure's
              // `draft` snapshot doesn't include the just-picked
              // brand because React hasn't re-rendered yet, so we
              // bypass the closure and pull the latest brand
              // directly from the external store.
              const fresh = store.getState();
              const info = getInfo?.(fresh.canonicalId) || null;
              const catalog = brandsForCanonical(fresh.canonicalId, {
                limit: 32, info, region,
              });
              const lower = (fresh.brand || "").trim().toLowerCase();
              const match = catalog.find((s) => s.display.toLowerCase() === lower);
              if (match?.variants && match.variants.length > 0) {
                setStep("variant");
              } else {
                setStep("size");
              }
            }}
            onSkip={() => setStep("size")}
          />
        </Step>
      )}

      {!showGlobalResults && step === "variant" && (() => {
        // Look up the active brand's variant list. We compute on
        // the fly rather than caching across the brand step's
        // commit so updates to the catalog (e.g. an admin promoted
        // a new variant for this brand mid-session) are picked up.
        const info = getInfo?.(draft.canonicalId) || null;
        const catalog = brandsForCanonical(draft.canonicalId, {
          limit: 32, info, region,
        });
        const lower = (draft.brand || "").trim().toLowerCase();
        const match = catalog.find((s) => s.display.toLowerCase() === lower);
        const variants = match?.variants || [];
        return (
          <Step
            kicker={`${(draft.brand || "BRAND").toUpperCase()} VARIANTS`}
            title={`Which ${draft.brand || "variant"}?`}
            accent={CHIP_TONES.ingredients.fg}
          >
            {variants.length === 0 ? (
              <div style={{
                fontFamily: font.sans,
                fontSize: 13,
                color: color.inkMuted,
                padding: "16px 0",
                textAlign: "center",
              }}>
                No variants registered for {draft.brand}.
              </div>
            ) : (
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
              }}>
                {variants.map((v) => {
                  const active = (draft.variant || "").toLowerCase() === v.name.toLowerCase();
                  return (
                    <button
                      key={v.name}
                      onClick={(e) => {
                        e.preventDefault();
                        store.setAxis("variant", v.name);
                        setStep("size");
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "12px 14px",
                        fontFamily: font.itemTitle,
                        fontStyle: "italic",
                        fontSize: 15,
                        fontWeight: 500,
                        color: active ? color.cream : color.ink,
                        background: active
                          ? CHIP_TONES.ingredients.fg
                          : color.cream,
                        border: `1px solid ${active ? CHIP_TONES.ingredients.fg : color.hairline}`,
                        borderRadius: radius.field || 12,
                        cursor: "pointer",
                        textAlign: "left",
                        minHeight: 64,
                      }}
                    >
                      <span style={{
                        flex: 1,
                        minWidth: 0,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        lineHeight: 1.25,
                      }}>
                        {v.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            <button
              onClick={(e) => {
                e.preventDefault();
                store.setAxis("variant", null);
                setStep("size");
              }}
              style={{
                alignSelf: "center",
                marginTop: 4,
                padding: "8px 14px",
                fontFamily: font.sans,
                fontSize: 12,
                color: color.inkMuted,
                background: "transparent",
                border: "none",
                cursor: "pointer",
                letterSpacing: "0.06em",
              }}
            >
              Skip — no variant
            </button>
          </Step>
        );
      })()}

      {!showGlobalResults && step === "size" && (
        <Step kicker="SIZE" title="How is it packaged?" accent={CHIP_TONES.canonical.fg}>
          <PackageSizeStep
            canonicalId={draft.canonicalId}
            brand={draft.brand}
            value={
              draft.packageAmount && draft.packageUnit
                ? { amount: draft.packageAmount, unit: draft.packageUnit }
                : null
            }
            onChange={({ amount, unit }) => {
              store.setAxis("packageAmount", amount);
              store.setAxis("packageUnit", unit);
            }}
            onCommit={() => setStep("done")}
          />
        </Step>
      )}

      {!showGlobalResults && step === "done" && (
        <Step kicker="READY" title="Ready to place." accent={CHIP_TONES.canonical.fg}>
          {/* The SHARED confirm surface — same chip-breadcrumb review
              the scan and photo pipelines end on, so the wizard stops
              being a third bespoke summary. Chips tap back into the
              wizard's own steps; both destination CTAs come from
              ScanReview itself. */}
          <ScanReview
            draft={draft}
            showHeader={false}
            hiddenAxes={["state", "cut"]}
            onOpenAxis={(axis) => {
              if (axis === "location" || axis === "tileId") {
                placementMoveRef.current = true;
                setStep("tile");
              }
              else if (axis === "hub" || axis === "canonicalId") {
                placementMoveRef.current = false;
                setActiveHubId(null);
                setStep("group");
              } else if (axis === "brand") { placementMoveRef.current = false; setStep("brand"); }
            }}
            onEditSize={() => setStep("size")}
            onPlace={() => store._onPlace?.()}
            onAddToList={() => store._onAddToList?.()}
          />
        </Step>
      )}

    </div>
  );
}

// Persistent Fridge / Pantry / Freezer tabs at the top of the
// wizard. Mirrors FloatingLocationDock's swatch palette + active-pill
// styling so the wizard reads as the same surface as the kitchen.
// Inline (not portal'd / fixed) because it lives inside the sheet's
// scroll container, not at the bottom of the viewport.
function LocationTabs({ active, onSelect }) {
  return (
    <div
      role="tablist"
      aria-label="Location"
      style={{
        display: "flex",
        gap: 4,
        padding: 4,
        borderRadius: 999,
        background: color.cream,
        border: `1px solid ${color.hairline}`,
      }}
    >
      {LOCATIONS.map((loc) => {
        const isActive = active === loc.id;
        const dot = LOCATION_DOT[loc.id] || color.inkMuted;
        return (
          <button
            key={loc.id}
            role="tab"
            aria-selected={isActive}
            onClick={(e) => { e.preventDefault(); onSelect(loc.id); }}
            style={{
              flex: 1,
              position: "relative",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              padding: "10px 14px",
              borderRadius: 999,
              border: isActive ? `1px solid ${dot}59` : "1px solid transparent",
              background: isActive
                ? `linear-gradient(${dot}2e, ${dot}2e), ${color.cream}`
                : "transparent",
              cursor: "pointer",
              fontFamily: font.sans,
              fontSize: 13,
              fontWeight: isActive ? 600 : 500,
              color: isActive ? color.ink : color.inkMuted,
              transition: "background 200ms ease, color 200ms ease, border-color 200ms ease",
              whiteSpace: "nowrap",
            }}
          >
            <span style={{
              display: "inline-block",
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: `radial-gradient(circle at 30% 25%, rgba(255,255,255,${isActive ? 0.55 : 0.28}) 0%, rgba(255,255,255,0) 55%), ${dot}`,
              boxShadow: isActive
                ? `0 0 0 3px ${dot}33, 0 1px 3px rgba(30,20,8,0.25)`
                : `0 1px 2px rgba(30,20,8,0.18), inset 0 1px 0 rgba(255,255,255,0.3)`,
              flexShrink: 0,
            }} />
            <span>{loc.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function Crumbs({ draft, step, onJump, activeHub }) {
  // Location is shown as a persistent tab above the wizard now —
  // omit it from the breadcrumb trail to avoid visual duplication.
  const crumbs = [];
  if (draft.tileId) {
    crumbs.push({ label: draft.tileId.replace(/_/g, " "), target: "tile" });
  }
  // Hub crumb. Earlier version omitted this whenever a canonical
  // was bound — that hid the user's drill path for hub members
  // (chicken_hub > chicken_thigh showed only "Chicken Thighs",
  // never "Chicken › Chicken Thighs").
  //
  // Source: prefer activeHub state (when user is browsing a hub
  // without having committed to a canonical yet). When a canonical
  // is bound, activeHubId gets nulled by pickCanonical, so derive
  // the hub from the canonical's parentId — that's the structural
  // source of truth and survives the activeHub-null reset.
  const canonicalLabel = draft.canonicalId
    ? (draft.canonicalName || draft.canonicalId)
    : null;
  let hubLabel = activeHub ? String(activeHub) : null;
  if (!hubLabel && draft.canonicalId) {
    const ing = findIngredient(draft.canonicalId);
    const parentHub = ing?.parentId ? findHub(ing.parentId) : null;
    if (parentHub?.name) hubLabel = parentHub.name;
  }
  if (hubLabel) {
    const sameAsCanonical =
      canonicalLabel &&
      canonicalLabel.toLowerCase().trim() ===
        hubLabel.toLowerCase().trim();
    if (!sameAsCanonical) {
      crumbs.push({ label: hubLabel, target: "group" });
    }
  }
  if (canonicalLabel) {
    crumbs.push({ label: canonicalLabel, target: "group" });
  }
  if (draft.brand) {
    crumbs.push({ label: draft.brand, target: "brand" });
  }
  if (draft.variant) {
    crumbs.push({ label: draft.variant, target: "variant" });
  }
  if (crumbs.length === 0) return null;
  return (
    <div style={{
      display: "flex",
      flexWrap: "wrap",
      gap: 6,
      paddingBottom: 4,
      borderBottom: `1px solid ${color.hairline}`,
    }}>
      {crumbs.map((c, i) => (
        <button
          key={i}
          onClick={(e) => { e.preventDefault(); onJump(c.target); }}
          style={{
            fontFamily: font.mono,
            fontSize: 10,
            letterSpacing: "0.10em",
            textTransform: "uppercase",
            color: color.inkMuted,
            background: "transparent",
            border: "none",
            padding: 0,
            cursor: "pointer",
          }}
        >
          {c.label}{i < crumbs.length - 1 ? " ›" : ""}
        </button>
      ))}
    </div>
  );
}

function Step({ kicker, title, accent, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{
          fontFamily: font.mono,
          fontSize: 10,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: accent,
        }}>
          {kicker}
        </div>
        <h2 style={{
          fontFamily: font.itemTitle,
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: 24,
          lineHeight: 1.05,
          color: color.ink,
          margin: 0,
        }}>
          {title}
        </h2>
      </div>
      {children}
    </div>
  );
}

function Tiles({ options, onPick }) {
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 8,
    }}>
      {options.map((opt) => (
        <button
          key={opt.id}
          onClick={(e) => { e.preventDefault(); onPick(opt); }}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: 4,
            padding: "14px 14px",
            background: color.cream,
            border: `1px solid ${color.hairline}`,
            borderRadius: radius.field || 12,
            cursor: "pointer",
            textAlign: "left",
            minHeight: 84,
          }}
        >
          {opt.emoji && (
            <div style={{ fontSize: 22, lineHeight: 1 }}>{opt.emoji}</div>
          )}
          <div style={{
            fontFamily: font.itemTitle,
            fontStyle: "italic",
            fontSize: 17,
            fontWeight: 500,
            color: color.ink,
          }}>
            {opt.label}
          </div>
          {opt.sub && (
            <div style={{
              fontFamily: font.sans,
              fontSize: 11,
              color: color.inkMuted,
              lineHeight: 1.3,
            }}>
              {opt.sub}
            </div>
          )}
        </button>
      ))}
    </div>
  );
}

